import React from 'react';
import { FileText, X } from 'lucide-react';
import PageHero from '@/components/site/PageHero';
import SectionHeader from '@/components/site/SectionHeader';
import ClosingCta from '@/components/site/ClosingCta';
import GoldText from '@/components/site/GoldText';
import { operatingCostNote, pilotComponents, pilotScopeNote } from '@/content/pilot';
import { usePageMeta } from '@/hooks/usePageMeta';

const goodFit = [
  'A repeatable task that happens often enough to evaluate.',
  'Information the assistant can work from, and that someone can approve.',
  'A manageable set of actions, with clear points for human review.',
  'An internal owner who can review results and give feedback.',
];

const included = [
  ['Written scope', 'The workflow, responsibilities, success criteria, and what is out of scope.'],
  ['Configured assistant', 'One assistant set up around the agreed task.'],
  ['Approved sources', 'The information the assistant uses, and who keeps it current.'],
  ['Scoped connections', 'Only the systems and actions named in the written scope.'],
  ['Tests', 'Representative scenarios, edge cases, and escalation behavior.'],
  ['Controlled evaluation', 'A bounded period of real use with your team’s feedback.'],
  ['Documentation', 'Operating instructions, configuration notes, and known limitations.'],
  ['Findings review', 'Results against the agreed measures, and a recommended next step.'],
];

const schedule = [
  ['Week 1', 'Workflow definition, baseline, access, and scope confirmation.'],
  ['Weeks 2–3', 'Configuration, scoped connections, and testing.'],
  ['Weeks 4–5', 'Controlled use, feedback, and refinement.'],
  ['Week 6', 'Evaluation, documentation, training, and next-step review.'],
];

const handoff = [
  'Operating instructions',
  'Configuration documentation',
  'Approved sources register',
  'Known limitations',
  'Evaluation findings',
  'Account and access arrangements',
];

const outcomes = [
  ['Continue operating', 'The pilot met its measures. Keep it running under the agreed operating arrangement.'],
  ['Revise the workflow', 'Parts worked. Adjust scope, sources, or review steps before continuing.'],
  ['Expand', 'Pursue further work through a separately scoped engagement.'],
  ['Stop', 'The results do not justify further work. That is a legitimate, useful answer.'],
];

const Pilot = () => {
  usePageMeta(
    'The AI Assistant Pilot',
    'A scoped pilot to define, build, test, and evaluate one AI assistant for one business workflow.',
  );

  return (
    <>
      <PageHero
        eyebrow="The AI Assistant Pilot"
        title={
          <>
            Your first AI assistant.
            <br />
            <GoldText>Built around one real workflow.</GoldText>
          </>
        }
        note="Begin with a free discovery call. Scope and operating costs are confirmed before kickoff."
      >
        <p>A scoped pilot to define, build, test, and evaluate an assistant for a specific business task.</p>
      </PageHero>

      {/* A. Fit */}
      <section aria-labelledby="fit-title" className="border-t border-rule">
        <div className="section-container grid gap-12 lg:grid-cols-2">
          <SectionHeader id="fit-title" title="What makes a suitable pilot?">
            <p>The best pilots are narrow enough to test properly and frequent enough to matter.</p>
          </SectionHeader>
          <div className="space-y-6">
            <ul className="space-y-4">
              {goodFit.map((item) => (
                <li key={item} className="flex gap-3">
                  <span aria-hidden="true" className="font-mono text-goldcore">
                    +
                  </span>
                  <span className="text-lg">{item}</span>
                </li>
              ))}
            </ul>
            <p className="flex gap-3 border border-rule bg-knightblack/60 p-5 text-mist">
              <X className="mt-0.5 h-5 w-5 shrink-0 text-victorycoral" aria-hidden="true" />A pilot is not an
              unrestricted replacement for an entire role, department, or business process.
            </p>
          </div>
        </div>
      </section>

      {/* B. Included */}
      <section aria-labelledby="included-title" className="border-t border-rule">
        <div className="section-container">
          <SectionHeader id="included-title" title="What is included?">
            <p>Six components, one assistant, one agreed workflow.</p>
          </SectionHeader>
          <ol className="reveal mt-12 grid gap-px overflow-hidden border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-3">
            {pilotComponents.map((c, i) => (
              <li key={c.name} className="bg-knightblack p-6">
                <p className="mb-4 font-mono text-xs tracking-[0.18em]">
                  <span className="gold-shine">0{i + 1}</span>
                </p>
                <h3 className="mb-2 text-2xl">{c.name}</h3>
                <p className="text-sm text-mist">{c.covers}</p>
              </li>
            ))}
          </ol>
          <h3 className="mb-4 mt-14 text-xl">In practice, that means</h3>
          <dl className="grid gap-x-10 gap-y-5 sm:grid-cols-2">
            {included.map(([term, desc]) => (
              <div key={term} className="border-l-2 border-goldcore/60 pl-4">
                <dt className="font-medium">{term}</dt>
                <dd className="text-sm text-mist">{desc}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-10 grid gap-4 text-sm text-mist md:grid-cols-2">
            <p className="surface p-5">
              <span className="font-medium text-softwhite">Scope. </span>
              {pilotScopeNote}
            </p>
            <p className="surface p-5">
              <span className="font-medium text-softwhite">Operating costs. </span>
              {operatingCostNote}
            </p>
          </div>
        </div>
      </section>

      {/* C. Schedule */}
      <section aria-labelledby="schedule-title" className="border-t border-rule">
        <div className="section-container grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeader id="schedule-title" title="Proposed delivery schedule">
            <p>
              A typical six-week structure. Access, feedback, and integration requirements affect the agreed schedule,
              which is set in your written scope.
            </p>
          </SectionHeader>
          <ol className="divide-y divide-rule border-y border-rule">
            {schedule.map(([period, focus]) => (
              <li key={period} className="grid gap-2 py-5 sm:grid-cols-[8rem_1fr]">
                <span className="font-mono text-xs uppercase tracking-[0.14em]">
                  <span className="gold-shine">{period}</span>
                </span>
                <span className="text-lg">{focus}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* D. Responsibilities */}
      <section aria-labelledby="resp-title" className="border-t border-rule">
        <div className="section-container">
          <SectionHeader id="resp-title" title="Responsibilities" />
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="surface p-6 sm:p-8">
              <p className="eyebrow mb-3">GoldCrusade</p>
              <p className="text-lg">
                Define and deliver the scoped implementation, test it, document its operation, and present findings.
              </p>
            </div>
            <div className="surface p-6 sm:p-8">
              <p className="eyebrow mb-3">Your team</p>
              <p className="text-lg">
                Provide approved information, suitable access, a workflow owner, and timely feedback.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* E. Handoff */}
      <section aria-labelledby="handoff-title" className="border-t border-rule">
        <div className="section-container grid gap-12 lg:grid-cols-2">
          <SectionHeader id="handoff-title" title="What you receive at handoff">
            <p>Ownership, licensing, and ongoing operating responsibilities are set out in your agreement.</p>
          </SectionHeader>
          <figure className="surface overflow-hidden" aria-label="Sample handoff bundle">
            <figcaption className="border-b border-rule px-5 py-3">
              <span className="meta-label">Sample handoff bundle</span>
            </figcaption>
            <ul className="divide-y divide-rule/70">
              {handoff.map((doc) => (
                <li key={doc} className="flex items-center gap-3 px-5 py-3.5">
                  <FileText className="h-4 w-4 text-goldcore" aria-hidden="true" />
                  {doc}
                </li>
              ))}
            </ul>
          </figure>
        </div>
      </section>

      {/* F. Next */}
      <section aria-labelledby="next-title" className="border-t border-rule">
        <div className="section-container">
          <SectionHeader id="next-title" title="What happens next?">
            <p>Every pilot ends with a decision, made from the evidence.</p>
          </SectionHeader>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {outcomes.map(([title, body]) => (
              <li key={title} className="surface p-6">
                <h3 className="mb-2 text-2xl">{title}</h3>
                <p className="text-sm text-mist">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <ClosingCta
        compact
        title={
          <>
            Start with a workflow—<GoldText>not a long-term commitment.</GoldText>
          </>
        }
        footnote="Free discovery call. Scope and costs confirmed before you proceed."
      />
    </>
  );
};

export default Pilot;
